import { useMemo, useState } from 'react'
import { Box, Checkbox, HStack, Text, Tooltip, VStack } from '@chakra-ui/react'
import type { TodoItem } from '../mock-data'

interface ITodoSidebarProps {
  todos: TodoItem[]
}

const TodoRow = ({ todo, onToggle }: { todo: TodoItem; onToggle: (id: string) => void }) => {
  return (
    <HStack
      spacing="10px"
      p="10px 12px"
      bg="#101a2d"
      border="1px solid rgba(255,255,255,0.08)"
      borderRadius="12px"
      align="flex-start"
    >
      <Checkbox isChecked={todo.completed} onChange={() => onToggle(todo.id)} colorScheme="blue" size="md" mt="2px" />
      <Box fontSize="16px" lineHeight="1.2">
        {todo.icon}
      </Box>
      <VStack spacing="4px" align="flex-start" flex={1} minW={0}>
        <Text
          color="white"
          fontSize="13px"
          fontWeight={600}
          m={0}
          textDecoration={todo.completed ? 'line-through' : 'none'}
          opacity={todo.completed ? 0.55 : 1}
          noOfLines={2}
        >
          {todo.title}
        </Text>
        <HStack spacing="6px">
          <Box
            px="8px"
            h="18px"
            borderRadius="6px"
            bg={`${todo.teamTag.color}22`}
            display="inline-flex"
            alignItems="center"
          >
            <Text fontSize="9px" fontWeight={700} color={todo.teamTag.color} m={0} letterSpacing="0.06em">
              {todo.teamTag.label.toUpperCase()}
            </Text>
          </Box>
        </HStack>
      </VStack>
      <Text color="rgba(255,255,255,0.55)" fontSize="11px" m={0} flexShrink={0}>
        {todo.due}
      </Text>
    </HStack>
  )
}

const TodoSidebar = ({ todos }: ITodoSidebarProps) => {
  const [localTodos, setLocalTodos] = useState<TodoItem[]>(todos)

  const toggle = (id: string) =>
    setLocalTodos(prev => prev.map(t => (t.id === id ? { ...t, completed: !t.completed } : t)))

  const active = useMemo(() => localTodos.filter(t => !t.completed), [localTodos])
  const completed = useMemo(() => localTodos.filter(t => t.completed), [localTodos])

  return (
    <Box bg="rgba(0, 0, 0, 0.15)" borderTop="1px solid rgba(194, 199, 214, 0.12)" borderRadius="16px" p="1px">
      <HStack
        justify="space-between"
        align="center"
        px="16px"
        py="8px"
        borderBottom="1px solid rgba(194, 199, 214, 0.08)"
      >
        <Text color="#ADB2BF" fontSize="12px" fontWeight={600} letterSpacing="0.02em" textTransform="uppercase" m={0}>
          To-do List
        </Text>
        <Box
          minW="22px"
          h="22px"
          px="8px"
          borderRadius="999px"
          bg="rgba(77,179,255,0.18)"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text fontSize="11px" fontWeight={700} color="#4DB3FF" m={0}>
            {active.length}
          </Text>
        </Box>
      </HStack>

      <Box p="16px">
        <VStack spacing="8px" align="stretch">
          {active.length === 0 ? (
            <Tooltip
              label="Coming soon — not available yet"
              hasArrow
              placement="top"
              bg="#1f2a3d"
              color="white"
              fontSize="12px"
              borderRadius="8px"
            >
              <Box
                p="16px"
                borderRadius="12px"
                border="1px solid rgba(255,255,255,0.08)"
                textAlign="center"
                opacity={0.55}
                cursor="help"
              >
                <Text color="rgba(255,255,255,0.55)" fontSize="12px" fontWeight={600} letterSpacing="0.08em" m={0}>
                  TBD
                </Text>
              </Box>
            </Tooltip>
          ) : (
            active.map(t => <TodoRow key={t.id} todo={t} onToggle={toggle} />)
          )}
        </VStack>

        {completed.length > 0 && (
          <Box mt="16px">
            <Text color="rgba(255,255,255,0.55)" fontSize="11px" fontWeight={700} letterSpacing="0.08em" m={0} mb="8px">
              COMPLETED ({completed.length})
            </Text>
            <VStack spacing="8px" align="stretch">
              {completed.map(t => (
                <TodoRow key={t.id} todo={t} onToggle={toggle} />
              ))}
            </VStack>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default TodoSidebar
